import { PlanogramModel } from './planogram.interfaces';
import { countAllProds } from './planogram-slim-save.wrapper';

/**
 * CATMAN-S38 — Rastro del autoguardado que falla antes de llegar al POST.
 *
 * Este fallo (excepción en el clonado, la comparación con el original o el precálculo de estantes)
 * es el único que no deja evidencia en ningún lado: no hay request, así que no aparece en la Network
 * del browser ni en los logs del backend, y el front no tiene telemetría propia. Lo único que queda
 * es la consola del usuario, así que conviene que ese log traiga de una todo lo necesario para
 * reproducir: qué explotó, en qué planograma y de qué tamaño.
 *
 * Nada de esto puede tirar una excepción: se ejecuta dentro del catch de un fallo que ya ocurrió,
 * sobre datos que por definición son sospechosos.
 */

/** Nombre de la propiedad global donde queda la última falla, para pedírsela a un usuario en vivo. */
export const LAST_AUTOSAVE_FAILURE_GLOBAL = '__spacesAutosaveLastFailure';

export interface AutosaveFailureReport {
  errorName: string;
  errorMessage: string;
  errorStack: string | null;
  planogramId: number | null;
  planogramName: string | null;
  moduleCount: number | null;
  prodCount: number | null;
}

export function buildAutosaveFailureReport(error: any, planogram?: PlanogramModel | null): AutosaveFailureReport {
  return {
    errorName: safe(() => String(error?.name ?? typeof error), 'unknown'),
    errorMessage: safe(() => String(error?.message ?? error), 'unknown'),
    errorStack: safe(() => (error?.stack ? String(error.stack) : null), null),
    planogramId: safe(() => numberOrNull(planogram?.id), null),
    planogramName: safe(() => (planogram?.planogramName ? String(planogram.planogramName) : null), null),
    moduleCount: safe(() => numberOrNull(planogram?.modules?.length), null),
    prodCount: safe(() => countAllProds(planogram), null),
  };
}

/**
 * Deja la última falla colgada del `window` para poder pedírsela por consola a un usuario que
 * todavía tiene la pantalla abierta. Sin esto, el único rastro se pierde apenas cierra el devtools
 * (o si nunca lo abrió, que es lo habitual).
 */
export function rememberLastAutosaveFailure(report: AutosaveFailureReport): void {
  try {
    if (typeof window === 'undefined') return;
    (window as any)[LAST_AUTOSAVE_FAILURE_GLOBAL] = report;
  } catch {
    //Un window de sólo lectura no puede romper el manejo del error que lo trajo hasta acá.
  }
}

function safe<T>(read: () => T, fallback: T): T {
  try {
    const value = read();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

function numberOrNull(value: any): number | null {
  //Number(null) es 0 y Number('') también: un planograma sin id reportaría "id 0", que en el log
  //se lee como un id real y manda a buscar el planograma equivocado.
  if (value == null || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
